import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import ScoreRing from '../components/ScoreRing';
import { toast } from 'sonner';
import { Radar, ArrowRight, History, Eye, X, Radio } from 'lucide-react';
import ModuleExplainer from '../components/ModuleExplainer';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Radar as A1, Bell as A2, Briefcase as A3, Award as A4, Calendar as A5, Filter as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/radar`;

const SEV = { high: '#f87171', medium: '#fbbf24', low: '#34d399' };
const URGENCY = { now: 'Act now', soon: 'Soon', 'this year': 'This year' };

export default function OpportunityRadarPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(API, getAuthHeader());
      setD(res.data);
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (key) => {
    const res = await axios.post(`${API}/acknowledge`, { signal_key: key }, getAuthHeader());
    setD(res.data);
    toast.success('Cleared from your radar.');
  };

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  const risk = d.risk || {};

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="radar-arrival"
        eyebrow="Opportunity Radar"
        title="The things you would otherwise miss."
        subtitle="Openings, awards, programmes and deadlines that fit your path, surfaced while there is still time to act, instead of found the week after they closed."
        accent="#60a5fa"
        second="#a78bfa"
        ctaLabel="Turn on my radar"
        narration={"This is Opportunity Radar. The most expensive thing in a career is the opportunity you never heard about. So this watches for the ones that fit your path, jobs, internships, scholarships, programmes and deadlines, and surfaces them while there is still time to act. Everything you keep becomes a dated step in your plan, so nothing quietly expires."}
        facts={[
          { icon: A1, head: "Matched to your path", sub: "Filtered by your target, level and location" },
          { icon: A5, head: "Early enough to act", sub: "Surfaced before the deadline, not after" },
          { icon: A2, head: "One place", sub: "Jobs, awards, programmes and dates together" }
        ]}
        modules={[
          { icon: A3, label: "Openings", line: "Roles and internships that fit where you are heading", hue: "#60a5fa" },
          { icon: A4, label: "Awards and funding", line: "Money with deadlines you can still make", hue: "#fbbf24" },
          { icon: A5, label: "Deadline watch", line: "Everything time-sensitive, in date order", hue: "#f472b6" },
          { icon: A6, label: "Tune it", line: "Tighten or widen what reaches you", hue: "#22d3ee" },
          { icon: A2, label: "Alerts", line: "Told once, clearly, when something matters", hue: "#a78bfa" },
          { icon: A1, label: "Saved to your plan", line: "Anything you keep becomes a dated step", hue: "#34d399" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Opportunity Radar"
        description="What changed, what it means for you, and what to do about it, before it becomes a problem."
      />

      <ModuleExplainer id="radar" />

      {/* Risk band */}
      <GlassCard className="mb-6" data-testid="risk-card">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <ScoreRing value={risk.score} label={`${risk.band} risk`} palette={
            risk.band === 'Low' ? 'emerald' : risk.band === 'Watch' ? 'cyan' : 'amber'
          } testId="risk-score" />
          <div className="flex-1 w-full">
            <div className="flex items-center gap-2 mb-1">
              <Radio className="w-4 h-4" style={{ color: risk.hue }} />
              <p className="text-sm font-semibold text-white">
                Career early-warning: <span style={{ color: risk.hue }}>{risk.band}</span>
                {risk.field ? <span className="text-slate-300 font-normal"> · {risk.field}</span> : null}
              </p>
            </div>
            <p className="text-xs text-slate-300 mb-4">{risk.note}</p>
            <div className="space-y-2">
              {(risk.drivers || []).map(dr => (
                <div key={dr.label} className="p-3 rounded-lg border bg-slate-800/40"
                  style={{ borderColor: `${SEV[dr.severity]}55`, boxShadow: `inset 0 0 24px ${SEV[dr.severity]}12` }}
                  data-testid={`risk-driver-${dr.label}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-white">{dr.label}</p>
                    <span className="text-[10px] uppercase tracking-[0.16em]" style={{ color: SEV[dr.severity] }}>{dr.severity}</span>
                  </div>
                  {dr.detail && <p className="text-xs text-slate-300 mt-1">{dr.detail}</p>}
                </div>
              ))}
              {(risk.drivers || []).length === 0 && (
                <p className="text-sm text-slate-300">No risk drivers detected against your current target.</p>
              )}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Opportunities we found */}
      {(d.opportunities || []).length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
              <Radar className="w-4 h-4 text-emerald-300" /> Opportunities we found for you
            </h3>
            <span className="text-xs text-slate-300">{d.opportunities.length} matched to your profile</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="radar-opportunities">
            {d.opportunities.map((o, i) => (
              <a key={`${o.name}-${i}`} href={o.url || '#'} target="_blank" rel="noreferrer"
                data-testid={`opportunity-${i}`}
                className="group relative overflow-hidden p-4 rounded-xl border bg-slate-800/40 hover:bg-slate-800/70 transition-colors duration-200"
                style={{ borderColor: `${o.hue}4d` }}>
                <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: o.hue }} />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: o.hue }}>{o.kind}</span>
                  {o.amount && <span className="text-xs font-semibold text-emerald-300 fp-num">{o.amount}</span>}
                </div>
                <p className="text-sm font-semibold text-white mt-2 leading-snug">{o.name}</p>
                {o.org && <p className="text-xs text-slate-300 mt-0.5">{o.org}</p>}
                {o.covers && <p className="text-xs text-slate-300 mt-1">{o.covers}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(o.why || []).map((w, j) => (
                    <span key={j} className="text-[10px] px-2 py-0.5 rounded-full border text-slate-200"
                      style={{ borderColor: `${o.hue}55` }}>{w}</span>
                  ))}
                </div>
                <p className="text-[11px] text-slate-300 mt-3">
                  {o.location ? `${o.location}` : ''}{o.deadline ? ` · deadline ${o.deadline}` : ''}
                </p>
              </a>
            ))}
          </div>
        </>
      )}

      {/* Signals */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
          <Radar className="w-4 h-4 text-cyan-300" /> Signals on your radar
        </h3>
        <span className="text-xs text-slate-300">{d.signal_count} active</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="signals-grid">
        {(d.signals || []).map(s => (
          <div key={s.key} data-testid={`signal-${s.key}`}
            className="group relative overflow-hidden p-5 rounded-xl border bg-slate-800/40 hover:bg-slate-800/70 transition-colors duration-200"
            style={{ borderColor: `${s.hue}4d` }}>
            <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: s.hue }} />
            <span className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-300"
              style={{ background: s.hue }} />
            <div className="relative flex items-start justify-between gap-3">
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: s.hue }}>{s.kind}</span>
                <p className="text-sm font-semibold text-white mt-1">{s.title}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] px-2 py-0.5 rounded-full text-slate-900 font-semibold" style={{ background: s.hue }}>
                  {URGENCY[s.urgency] || s.urgency}
                </span>
                <button onClick={() => dismiss(s.key)} data-testid={`dismiss-${s.key}`}
                  className="text-slate-300 hover:text-white transition-colors duration-200" title="Clear from radar">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <p className="relative text-xs text-slate-300 mt-3 leading-relaxed">
              <span className="text-slate-300">Why now, </span>{s.why_now}
            </p>
            <Link to={s.path} className="relative mt-4 inline-flex items-center gap-1 text-xs group-hover:gap-2 transition-all duration-200"
              style={{ color: s.hue }} data-testid={`signal-action-${s.key}`}>
              {s.action} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ))}
        {(d.signals || []).length === 0 && (
          <GlassCard><p className="text-sm text-slate-300">Radar is clear. Nothing needs your attention right now.</p></GlassCard>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard data-testid="triggers-card">
          <h3 className="text-base md:text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <History className="w-4 h-4 text-violet-300" /> Your situation changed, so we recalculated
          </h3>
          <p className="text-xs text-slate-300 mb-4">Nothing here ever restarts your profile. It updates around it.</p>
          {(d.triggers || []).length === 0 ? (
            <p className="text-sm text-slate-300">No changes detected yet. This log fills itself as your life moves.</p>
          ) : (
            <ol className="relative space-y-3 pl-4 border-l border-slate-700">
              {d.triggers.map(t => (
                <li key={t.id} className="relative">
                  <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-violet-400"
                    style={{ boxShadow: '0 0 10px rgba(167,139,250,.8)' }} />
                  <p className="text-sm text-slate-200">{t.summary}</p>
                  <p className="text-[11px] text-slate-300">{new Date(t.at).toLocaleString()}</p>
                </li>
              ))}
            </ol>
          )}
        </GlassCard>

        <GlassCard data-testid="watching-card">
          <h3 className="text-base md:text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <Eye className="w-4 h-4 text-cyan-300" /> What the radar watches for you
          </h3>
          <p className="text-xs text-slate-300 mb-4">{d.note}</p>
          <div className="space-y-2">
            {(d.watching || []).map(w => (
              <div key={w.label} className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-slate-200">{w.label}</p>
                <p className="text-[11px] text-slate-300">{w.source}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </AppLayout>
  );
}
